import {TriangleAlert} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import {AudienceConflictDto} from '@tinct/core-api-models';

type AudienceConflictModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflicts: AudienceConflictDto[];
  onExcludeAndContinue: () => void;
  isPending?: boolean;
};

export const AudienceConflictModal = ({
  open,
  onOpenChange,
  conflicts,
  onExcludeAndContinue,
  isPending,
}: AudienceConflictModalProps) => {
  const count = conflicts.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] rounded-2xl p-8 gap-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            Audience conflict detected
          </DialogTitle>
        </DialogHeader>

        {/* Alert */}
        <div className="flex gap-4 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <div className="flex flex-col gap-2 text-sm">
            <p className="font-medium">
              Some companies are already targeted on the same page
            </p>
            <p>
              The same company cannot be targeted by multiple campaigns on the same landing page URL.
              {' '}This concerns {count} {count === 1 ? 'company' : 'companies'} :
            </p>
          </div>
        </div>

        {/* Conflict list */}
        <Card className="py-0 overflow-hidden">
          <CardContent className="flex flex-col gap-3 p-6 max-h-[40vh] overflow-y-auto">
            {conflicts.map((conflict) => (
              <div key={`${conflict.company.id}-${conflict.campaign.id}`} className="flex items-center gap-4">
                <Avatar className="size-6 rounded-full">
                  {conflict.company.logo?.url && (
                    <AvatarImage src={conflict.company.logo.url} alt={conflict.company.name} />
                  )}
                  <AvatarFallback className="rounded-full text-xs">
                    {conflict.company.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0">
                  <p className="truncate text-sm font-medium">{conflict.company.name}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    in {conflict.campaign.name}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={onExcludeAndContinue} disabled={isPending}>
            {isPending ? 'Excluding...' : `Exclude ${count} ${count === 1 ? 'company' : 'companies'} and continue`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
